import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CalendarPlus, Info } from "lucide-react";
import { toast } from "sonner";
import { createPlansBulk, fmt } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";

const isoPlusDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const roundUp10 = (v) => Math.ceil(v / 10) * 10;

/**
 * "Add recommendations to the calendar" — every product the planner says to produce becomes a row
 * with editable quantity (kg), milk litres (auto-derived from the product yield, editable) and date.
 * Unchecked rows are skipped. Creates the plans in one call and hands control back to the calendar.
 */
export const PlanFromRecommendationsDialog = ({ open, rows, onlyProduct, horizon, onClose, onCreated }) => {
  const { t, td } = useT();
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [allDate, setAllDate] = useState(isoPlusDays(1));

  useEffect(() => {
    if (!open) return;
    const start = isoPlusDays(1);
    setAllDate(start);
    setItems((rows || []).filter((r) => r.recommend_kg > 0).map((r) => ({
      product_type: r.product_type,
      include: onlyProduct ? r.product_type === onlyProduct : true,
      kg: String(r.recommend_kg ?? ""),
      milk_l: r.suggested_milk_l != null ? String(r.suggested_milk_l) : "",
      yield: r.yield_per_100l || null,
      yield_source: r.yield_source,
      date: start,
      demand_kg: r.demand_kg, usable_kg: r.usable_kg, at_risk_kg: r.at_risk_kg,
      planned_milk_l: r.planned_milk_l || 0, planned_count: r.planned_count || 0,
      recommend_pkg_qty: r.recommend_pkg_qty, recommend_unit: r.recommend_unit,
    })));
  }, [open, rows, onlyProduct]);

  const update = (pt, patch) => setItems((xs) => xs.map((x) => (x.product_type === pt ? { ...x, ...patch } : x)));
  const setKg = (it, v) => {
    const kg = Number(v);
    const patch = { kg: v };
    if (it.yield && v !== "" && kg >= 0) patch.milk_l = String(roundUp10(kg / it.yield * 100));
    update(it.product_type, patch);
  };
  const setMilk = (it, v) => {
    const l = Number(v);
    const patch = { milk_l: v };
    if (it.yield && v !== "" && l >= 0) patch.kg = String(Math.round(l * it.yield / 100 * 10) / 10);
    update(it.product_type, patch);
  };
  const applyAllDate = (v) => { setAllDate(v); if (v) setItems((xs) => xs.map((x) => ({ ...x, date: v }))); };

  const selected = useMemo(() => items.filter((x) => x.include), [items]);
  const totalMilk = selected.reduce((a, x) => a + (Number(x.milk_l) || 0), 0);
  const totalKg = selected.reduce((a, x) => a + (Number(x.kg) || 0), 0);
  const missingMilk = selected.filter((x) => !x.milk_l || Number(x.milk_l) <= 0).length;

  const submit = async () => {
    if (selected.length === 0) { toast.error(t("planner.addNoneSelected")); return; }
    if (selected.some((x) => !x.date)) { toast.error(t("planning.productDateRequired")); return; }
    setSaving(true);
    try {
      const payload = selected.map((x) => ({
        product_type: x.product_type,
        planned_date: x.date,
        planned_volume_l: x.milk_l === "" ? null : Number(x.milk_l),
        notes: t("planner.addNote", { kg: fmt(Number(x.kg) || 0), demand: fmt(x.demand_kg), usable: fmt(x.usable_kg), horizon }),
      }));
      const res = await createPlansBulk(payload, "planner");
      toast.success(t("planner.addDone", { n: res.count }));
      onCreated?.(res);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("planning.failedSavePlan"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-3xl" data-testid="planner-add-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarPlus className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("planner.addTitle")}</DialogTitle>
          <DialogDescription>{t("planner.addDesc")}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="planner-all-date" className="text-xs text-muted-foreground">{t("planner.addAllDate")}</Label>
            <Input id="planner-all-date" type="date" value={allDate} onChange={(e) => applyAllDate(e.target.value)} className="w-[170px]" data-testid="planner-add-all-date" />
          </div>
          <div className="text-xs text-muted-foreground">
            <label className="inline-flex cursor-pointer items-center gap-2">
              <Checkbox checked={selected.length === items.length && items.length > 0} onCheckedChange={(v) => setItems((xs) => xs.map((x) => ({ ...x, include: !!v })))} data-testid="planner-add-select-all" />
              {t("planner.addSelectAll")}
            </label>
          </div>
        </div>

        <div className="overflow-x-auto rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>{t("common.product")}</TableHead>
                <TableHead className="text-right">{t("planner.addKg")}</TableHead>
                <TableHead className="text-right">{t("planner.addMilk")}</TableHead>
                <TableHead>{t("planning.plannedDate")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">{t("planner.addEmpty")}</TableCell></TableRow>
              ) : items.map((it) => (
                <TableRow key={it.product_type} className={it.include ? "" : "opacity-50"} data-testid={`planner-add-row-${it.product_type}`}>
                  <TableCell className="pr-0">
                    <Checkbox checked={it.include} onCheckedChange={(v) => update(it.product_type, { include: !!v })} data-testid={`planner-add-include-${it.product_type}`} />
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{td(it.product_type)}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {t("planner.addRecommended", { kg: fmt(it.demand_kg - it.usable_kg > 0 ? it.demand_kg - it.usable_kg : 0) })}
                      {it.recommend_pkg_qty ? ` · ≈ ${it.recommend_pkg_qty} × ${it.recommend_unit}` : ""}
                    </div>
                    <div className="mt-0.5 flex flex-wrap gap-1">
                      {it.yield ? (
                        <Badge variant="secondary" className="text-[10px]" data-testid={`planner-add-yield-${it.product_type}`}>
                          {t(it.yield_source === "target" ? "planner.addYieldTarget" : "planner.addYieldActual", { y: fmt(it.yield, 1) })}
                        </Badge>
                      ) : (
                        <Badge className="badge-warn border-transparent text-[10px]"><Info className="mr-1 h-3 w-3" /> {t("planner.addNoYield")}</Badge>
                      )}
                      {it.planned_count > 0 && (
                        <Badge variant="secondary" className="text-[10px]">{t("planner.addAlreadyPlanned", { l: fmt(it.planned_milk_l, 0), n: it.planned_count })}</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Input type="number" min="0" step="0.5" value={it.kg} onChange={(e) => setKg(it, e.target.value)} disabled={!it.include}
                      className="ml-auto w-[110px] text-right font-mono" data-testid={`planner-add-kg-${it.product_type}`} />
                  </TableCell>
                  <TableCell className="text-right">
                    <Input type="number" min="0" step="10" value={it.milk_l} onChange={(e) => setMilk(it, e.target.value)} disabled={!it.include}
                      placeholder="—" className="ml-auto w-[110px] text-right font-mono" data-testid={`planner-add-milk-${it.product_type}`} />
                  </TableCell>
                  <TableCell>
                    <Input type="date" value={it.date} onChange={(e) => update(it.product_type, { date: e.target.value })} disabled={!it.include}
                      className="w-[160px]" data-testid={`planner-add-date-${it.product_type}`} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
          <span data-testid="planner-add-summary">{t("planner.addSummary", { n: selected.length, kg: fmt(totalKg), l: fmt(totalMilk, 0) })}</span>
          {missingMilk > 0 && <span className="text-[hsl(var(--warning))]">{t("planner.addMissingMilk", { n: missingMilk })}</span>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="planner-add-cancel">{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={saving || selected.length === 0} data-testid="planner-add-submit">
            <CalendarPlus className="mr-1.5 h-4 w-4" /> {saving ? t("common.saving") : t("planner.addSubmit", { n: selected.length })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
